import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { 
  referralAPI, 
  clientAPI,
  contactAPI,
  lookupAPI,
  staffAPI,
  providerAPI
} from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import {
  formatProviderDisplayLabel,
  formatProviderSiteOptionLabel,
  formatPcpOptionLabelFromReferral,
  formatPcpOptionLabelFromClient,
} from '../utils/providerDisplayLabel';
import SearchableProviderTeamOfficeSelect from './SearchableProviderTeamOfficeSelect';
import ReferralInsuranceWorkspaceSection from './ReferralInsuranceWorkspaceSection';
import './ReferralForm.css';

function ReferralForm() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const pathname = location?.pathname || '';
  const isNestedUnderClient = pathname.includes('/client/') && pathname.includes('/referral');
  const referralId = isNestedUnderClient ? params.referralId : params.id;
  const clientIdFromPath = isNestedUnderClient ? params.id : null;
  const clientId = clientIdFromPath || searchParams.get('clientId') || null;
  const reReferralParam = searchParams.get('reReferral') === 'true';
  const fromNewClientReferral = searchParams.get('from') === 'new-client-referral';
  const isEdit = !!referralId;
  const hasPrefilledFromClientRef = useRef(false);
  const priorReferralPrefillClientIdRef = useRef(null);
  const referralDxPrefillKeyRef = useRef('');
  const insuranceWorkspaceSaveRef = useRef(null);
  /** Stable callback so insurance child’s useEffect does not clear/re-register save on every parent render. */
  const registerInsuranceWorkspaceSave = useCallback((fn) => {
    insuranceWorkspaceSaveRef.current = fn;
  }, []);

  // Create mode requires referrals.create (e.g. View as service creator may not have it)
  const canCreate = hasPermission('referrals.create');
  const canCreateProvider = hasPermission('providers.create');
  const canCreateContact = hasPermission('contacts.create');
  const canUpdateReferral = hasPermission('referrals.update');
  const canEditTermination = hasPermission('referrals.edit_termination');
  /** Settings → Referral lookups → Insurance Companies (add row); same gate as editing that lookup table. */
  const canManageInsuranceCompaniesLookup =
    hasPermission('settings.view') && hasPermission('settings.manage_lookups');
  const showCreateDenied = !isEdit && !canCreate;

  const REFERRAL_FORM_DRAFT_KEY = 'cfc_referral_form_draft';

  // Initialize form data (re_referral true when arriving via New Client / Referral → Re-referral)
  const [formData, setFormData] = useState({
    client_id: clientId || '',
    referral_date: new Date().toISOString().split('T')[0],
    referral_reason: '',
    open_date: new Date().toISOString().split('T')[0],
    intake_notes: '',
    referral_sc_comment: '',
    intake_staff_id: '',
    close_date: '',
    termination_reason_id: '',
    insurance_company_id: '',
    insurance_type_id: '',
    primary_care_physician_id: '',
    primary_care_physician_site_id: '',
    living_arrangement_id: '',
    referral_level_1_id: '',
    referral_level_2_id: '',
    household_size: '',
    referral_phone: '',
    re_referral: reReferralParam,
    original_sc_id: '',
    sibling_in_ei: false,
    sibling_sc_id: '',
    screened: false,
    referral_level_2_provider_id: '',
    diagnosis_1_id: '',
    referral_contact_id: '',
    referral_relationship_id: '',
    referral_status: 'incomplete',
  });

  // Lookup data
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [terminationReasons, setTerminationReasons] = useState([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [livingArrangements, setLivingArrangements] = useState([]);
  const [referralLevel1, setReferralLevel1] = useState([]);
  const [referralLevel2, setReferralLevel2] = useState([]);
  const [providers, setProviders] = useState([]);
  const [pcpSiteOptions, setPcpSiteOptions] = useState([]);
  const [diagnosisAutoeligible, setDiagnosisAutoeligible] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isClosed, setIsClosed] = useState(false);
  const [isEditingTermination, setIsEditingTermination] = useState(false);
  const [terminationSaving, setTerminationSaving] = useState(false);
  const originalTerminationRef = useRef(null);

  /** Main referral fields (PUT /referrals/:id) — insurance workspace tracked separately */
  const referralUnsavedMainRef = useRef(false);
  const insuranceWorkspaceDirtyRef = useRef(false);

  const clearReferralUnsavedMarkers = useCallback(() => {
    referralUnsavedMainRef.current = false;
    insuranceWorkspaceDirtyRef.current = false;
  }, []);

  const markReferralMainDirty = useCallback(() => {
    if (!isEdit || isClosed || !canUpdateReferral) return;
    referralUnsavedMainRef.current = true;
  }, [isEdit, isClosed, canUpdateReferral]);

  const handleInsuranceWorkspaceDirtyChange = useCallback(
    (dirty) => {
      if (!isEdit || isClosed || !canUpdateReferral) return;
      insuranceWorkspaceDirtyRef.current = !!dirty;
    },
    [isEdit, isClosed, canUpdateReferral]
  );

  /* useBlocker requires createBrowserRouter / RouterProvider; this app uses BrowserRouter.
   * We still warn on refresh/close (beforeunload) and on same-tab clicks to internal <a href> links (capture). */
  useEffect(() => {
    if (!isEdit || isClosed || !canUpdateReferral) return undefined;
    const onBeforeUnload = (e) => {
      if (!referralUnsavedMainRef.current && !insuranceWorkspaceDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isEdit, isClosed, canUpdateReferral]);

  useEffect(() => {
    if (!isEdit || isClosed || !canUpdateReferral) return undefined;
    const LEAVE_MSG =
      'You have unsaved changes on this referral. If you leave now, those changes will be discarded.\n\nLeave without saving?';

    const onClickCapture = (e) => {
      if (!referralUnsavedMainRef.current && !insuranceWorkspaceDirtyRef.current) return;
      const a = e.target?.closest?.('a[href]');
      if (!a) return;
      if (a.target === '_blank' || a.hasAttribute('download')) return;
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const href = a.getAttribute('href');
      if (href == null || href === '' || href.startsWith('#')) return;
      if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;

      let nextUrl;
      try {
        nextUrl = new URL(a.href, window.location.origin);
      } catch (_) {
        return;
      }
      if (nextUrl.origin !== window.location.origin) return;

      const next = `${nextUrl.pathname}${nextUrl.search}`;
      const cur = `${window.location.pathname}${window.location.search}`;
      if (next === cur) return;

      if (!window.confirm(LEAVE_MSG)) {
        e.preventDefault();
        e.stopPropagation();
      } else {
        clearReferralUnsavedMarkers();
      }
    };

    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [isEdit, isClosed, canUpdateReferral, clearReferralUnsavedMarkers]);

  const [showReReferralInfo, setShowReReferralInfo] = useState(reReferralParam);
  const [showOriginalScInfo, setShowOriginalScInfo] = useState(false);
  const [showSiblingInEiInfo, setShowSiblingInEiInfo] = useState(false);
  const [showSiblingScInfo, setShowSiblingScInfo] = useState(false);
  const [showOpenReferralDateInfo, setShowOpenReferralDateInfo] = useState(false);
  // When referral has a PCP not in the paginated providers list, show this label so the dropdown can display it
  const [referralPcpDisplayLabel, setReferralPcpDisplayLabel] = useState(null);
  // Same for referral contact (contacts list is paginated) and insurance (in case id not in lookup list)
  const [referralContactDisplayLabel, setReferralContactDisplayLabel] = useState(null);
  const [referralInsuranceCompanyDisplayLabel, setReferralInsuranceCompanyDisplayLabel] = useState(null);
  const [referralInsuranceTypeDisplayLabel, setReferralInsuranceTypeDisplayLabel] = useState(null);
  const [referralLevel2ProviderDisplayLabel, setReferralLevel2ProviderDisplayLabel] = useState(null);

  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [contactSearchLoading, setContactSearchLoading] = useState(false);
  const [contactDropdownOpen, setContactDropdownOpen] = useState(false);
  const contactCloseTimerRef = useRef(null);
  const [rl2ProviderSearchTerm, setRl2ProviderSearchTerm] = useState('');
  const [rl2ProviderDropdownOpen, setRl2ProviderDropdownOpen] = useState(false);
  const rl2ProviderCloseTimerRef = useRef(null);
  const rl2ProviderInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    loadOptions();
    if (isEdit) {
      loadReferral();
    } else if (clientId) {
      loadClient(clientId);
    }
  }, [referralId, clientId]);

  // Restore referral form draft when returning from provider creation
  useEffect(() => {
    if (isEdit) return;
    try {
      const raw = sessionStorage.getItem(REFERRAL_FORM_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.formData) setFormData(draft.formData);
      if (draft?.referralPcpDisplayLabel != null) setReferralPcpDisplayLabel(draft.referralPcpDisplayLabel);
      if (draft?.referralContactDisplayLabel != null) setReferralContactDisplayLabel(draft.referralContactDisplayLabel);
      sessionStorage.removeItem(REFERRAL_FORM_DRAFT_KEY);
    } catch (_) {
      sessionStorage.removeItem(REFERRAL_FORM_DRAFT_KEY);
    }
  }, [isEdit]);

  // After "Save and add referral": pre-fill PCP from client pediatrician only (not referral contact or referral phone).
  useEffect(() => {
    if (isEdit || !clientId || !fromNewClientReferral || !selectedClient || hasPrefilledFromClientRef.current) return;
    const pediatricianId = selectedClient.pediatrician_id != null && selectedClient.pediatrician_id !== '' ? String(selectedClient.pediatrician_id) : '';
    const pedPayeeSite =
      selectedClient.pediatrician_payee_site_id != null && selectedClient.pediatrician_payee_site_id !== ''
        ? String(selectedClient.pediatrician_payee_site_id)
        : '';
    const pedLegacySite =
      selectedClient.pediatrician_site_id != null && selectedClient.pediatrician_site_id !== ''
        ? String(selectedClient.pediatrician_site_id)
        : '';
    const pediatricianSiteId = pedPayeeSite || pedLegacySite || (pediatricianId ? `provider:${pediatricianId}` : '');
    const pcpDisplayLabel = formatPcpOptionLabelFromClient(selectedClient) || null;
    setFormData((prev) => {
      const next = { ...prev };
      if (pediatricianId) {
        next.primary_care_physician_id = pediatricianId;
        next.primary_care_physician_site_id = pediatricianSiteId;
      }
      return next;
    });
    if (pcpDisplayLabel) setReferralPcpDisplayLabel(pcpDisplayLabel);
    hasPrefilledFromClientRef.current = true;
  }, [isEdit, clientId, fromNewClientReferral, selectedClient]);

  // Re-referral: when a client is selected, pre-populate fields from their most recent prior referral.
  useEffect(() => {
    if (isEdit || !reReferralParam || !selectedClient?.id) return;
    if (priorReferralPrefillClientIdRef.current === selectedClient.id) return;
    priorReferralPrefillClientIdRef.current = selectedClient.id;
    referralAPI.getByClientId(selectedClient.id).then((res) => {
      const prior = (res.data?.data ?? res.data ?? [])[0];
      if (!prior) return;
      setFormData((prev) => {
        const next = { ...prev };
        if (!next.primary_care_physician_id && prior.primary_care_physician_id) {
          next.primary_care_physician_id = String(prior.primary_care_physician_id);
          const raw = prior.primary_care_physician_payee_site_id ?? prior.primary_care_physician_site_id ?? '';
          next.primary_care_physician_site_id = raw !== '' && raw != null
            ? String(raw)
            : `provider:${prior.primary_care_physician_id}`;
        }
        if (!next.insurance_company_id && prior.insurance_company_id) {
          next.insurance_company_id = String(prior.insurance_company_id);
        }
        if (!next.insurance_type_id && prior.insurance_type_id) {
          next.insurance_type_id = String(prior.insurance_type_id);
        }
        if (!next.referral_contact_id && prior.referral_contact_id) {
          next.referral_contact_id = String(prior.referral_contact_id);
        }
        if (!next.referral_reason && prior.referral_reason) {
          next.referral_reason = prior.referral_reason;
        }
        return next;
      });
      if (prior.primary_care_physician_id) {
        setReferralPcpDisplayLabel(formatPcpOptionLabelFromReferral(prior) || null);
      }
      if (prior.referral_contact_id && prior.referral_contact_name) {
        setReferralContactDisplayLabel(prior.referral_contact_name);
      }
      if (prior.insurance_company_id && prior.insurance_company_name) {
        setReferralInsuranceCompanyDisplayLabel(prior.insurance_company_name);
      }
      if (prior.insurance_type_id && prior.insurance_type_description) {
        setReferralInsuranceTypeDisplayLabel(prior.insurance_type_description);
      }
    }).catch(() => {});
  }, [isEdit, reReferralParam, selectedClient]);

  // When we have PCP site options and primary_care_physician_id but no primary_care_physician_site_id, select first matching site or synthetic "no site" option
  useEffect(() => {
    if (pcpSiteOptions.length === 0 || !formData.primary_care_physician_id || formData.primary_care_physician_site_id) return;
    const first = pcpSiteOptions.find((o) => String(o.provider_id) === String(formData.primary_care_physician_id));
    if (first) {
      setFormData((prev) => (prev.primary_care_physician_site_id ? prev : { ...prev, primary_care_physician_site_id: String(first.payee_site_id ?? first.provider_site_id) }));
    } else {
      setFormData((prev) => (prev.primary_care_physician_site_id ? prev : { ...prev, primary_care_physician_site_id: `provider:${formData.primary_care_physician_id}` }));
    }
  }, [pcpSiteOptions, formData.primary_care_physician_id, formData.primary_care_physician_site_id]);

  useEffect(() => {
    referralDxPrefillKeyRef.current = '';
  }, [referralId, clientId]);

  // Prefill referral Diagnosis Dx1 from the client when the referral row has no diagnosis yet (once per client/referral).
  useEffect(() => {
    if (!selectedClient?.id) return;
    const key = isEdit && referralId ? `edit:${referralId}` : `new:${selectedClient.id}`;
    if (referralDxPrefillKeyRef.current === key) return;
    referralDxPrefillKeyRef.current = key;
    setFormData((prev) => {
      if (prev.diagnosis_1_id) return prev;
      const d = selectedClient.diagnosis_1_id;
      if (d == null || d === '') return prev;
      return { ...prev, diagnosis_1_id: String(d) };
    });
  }, [selectedClient, isEdit, referralId]);

  const runClientSearch = useCallback((q) => {
    if (!q || String(q).trim().length < 2) {
      setClientResults([]);
      return;
    }
    setClientSearchLoading(true);
    clientAPI.getAll({ q: String(q).trim(), page: 1, pageSize: 500 })
      .then((res) => { setClientResults(res.data?.data ?? []); })
      .catch(() => setClientResults([]))
      .finally(() => setClientSearchLoading(false));
  }, []);

  const runContactSearch = useCallback((q) => {
    if (!q || String(q).trim().length < 2) {
      setContactResults([]);
      return;
    }
    setContactSearchLoading(true);
    contactAPI.getAll({ q: String(q).trim(), page: 1, pageSize: 500 })
      .then((res) => { setContactResults(res.data?.data ?? []); })
      .catch(() => setContactResults([]))
      .finally(() => setContactSearchLoading(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runClientSearch(clientSearch), 300);
    return () => clearTimeout(t);
  }, [clientSearch, runClientSearch]);

  useEffect(() => {
    const t = setTimeout(() => runContactSearch(contactSearch), 300);
    return () => clearTimeout(t);
  }, [contactSearch, runContactSearch]);

  const loadOptions = async () => {
    setLoadingOptions(true);
    
    const loadData = async (apiCall, name, setter) => {
      try {
        const response = await apiCall();
        const data = response?.data?.data || [];
        setter(data);
        console.log(`✅ Loaded ${data.length} ${name}`);
        return true;
      } catch (error) {
        console.error(`❌ Error loading ${name}:`, error.response?.data || error.message);
        setter([]);
        return false;
      }
    };

    await Promise.allSettled([
      loadData(() => clientAPI.getAll({ page: 1, pageSize: 500 }), 'clients', setClients),
      loadData(() => staffAPI.getAll({ active: 'true' }), 'staff', setStaff),
      loadData(() => lookupAPI.getTerminationReasons(), 'termination reasons', setTerminationReasons),
      loadData(() => lookupAPI.getInsuranceCompanies(), 'insurance companies', setInsuranceCompanies),
      loadData(() => lookupAPI.getInsuranceTypes(), 'insurance types', setInsuranceTypes),
      loadData(() => lookupAPI.getLivingArrangements(), 'living arrangements', setLivingArrangements),
      loadData(() => lookupAPI.getReferralLevel1(), 'referral level 1', setReferralLevel1),
      loadData(() => lookupAPI.getReferralLevel2(), 'referral level 2', setReferralLevel2),
      loadData(() => providerAPI.getAll({ page: 1, pageSize: 500 }), 'providers', setProviders),
      loadData(() => providerAPI.getByTypeSites('DR'), 'PCP site options', setPcpSiteOptions),
      loadData(() => lookupAPI.getDiagnosisAutoeligible(), 'diagnosis (auto-eligible)', setDiagnosisAutoeligible),
      loadData(() => contactAPI.getAll({ page: 1, pageSize: 500 }), 'contacts', setContacts),
    ]);

    setLoadingOptions(false);
  };

  const loadClient = async (cid) => {
    try {
      const response = await clientAPI.getById(cid);
      setSelectedClient(response.data.data);
    } catch (error) {
      console.error('Error loading client:', error);
    }
  };

  const loadReferral = async () => {
    try {
      setLoading(true);
      const response = await referralAPI.getById(referralId);
      const referral = response.data.data;

      const hasCloseDate = !!referral.close_date;
      setIsClosed(hasCloseDate);
      if (hasCloseDate) {
        originalTerminationRef.current = {
          close_date: referral.close_date ? referral.close_date.split('T')[0] : '',
          termination_reason_id: referral.termination_reason_id || '',
        };
      }

      const openReferralDate = referral.open_date ? referral.open_date.split('T')[0] : (referral.referral_date ? referral.referral_date.split('T')[0] : '');
      setFormData({
        client_id: referral.client_id || '',
        referral_date: openReferralDate,
        referral_reason: referral.referral_reason || '',
        open_date: openReferralDate,
        intake_notes: referral.intake_notes || '',
        referral_sc_comment: referral.referral_sc_comment || '',
        intake_staff_id: referral.intake_staff_id || '',
        close_date: referral.close_date ? referral.close_date.split('T')[0] : '',
        termination_reason_id: referral.termination_reason_id || '',
        insurance_company_id: referral.insurance_company_id || '',
        insurance_type_id: referral.insurance_type_id || '',
        primary_care_physician_id: referral.primary_care_physician_id || '',
        primary_care_physician_site_id: (() => {
          const raw = referral.primary_care_physician_payee_site_id ?? referral.primary_care_physician_site_id ?? '';
          if (raw !== '' && raw != null) return String(raw);
          if (referral.primary_care_physician_id) return `provider:${referral.primary_care_physician_id}`;
          return '';
        })(),
        living_arrangement_id: referral.living_arrangement_id || '',
        referral_level_1_id: referral.referral_level_1_id || '',
        referral_level_2_id: referral.referral_level_2_id || '',
        household_size: referral.household_size !== null && referral.household_size !== undefined ? String(referral.household_size) : '',
        referral_phone: referral.referral_phone || '',
        re_referral: referral.re_referral === true,
        original_sc_id: referral.original_sc_id || '',
        sibling_in_ei: referral.sibling_in_ei === true,
        sibling_sc_id: referral.sibling_sc_id || '',
        screened: referral.screened === true,
        referral_level_2_provider_id: referral.referral_level_2_provider_id || '',
        diagnosis_1_id: referral.diagnosis_1_id != null ? String(referral.diagnosis_1_id) : '',
        referral_contact_id: referral.referral_contact_id != null ? String(referral.referral_contact_id) : '',
        referral_relationship_id: referral.referral_relationship_id != null ? String(referral.referral_relationship_id) : '',
        referral_status: referral.referral_status || 'incomplete',
      });

      if (referral.primary_care_physician_id) {
        setReferralPcpDisplayLabel(formatPcpOptionLabelFromReferral(referral) || null);
      } else {
        setReferralPcpDisplayLabel(null);
      }
      if (referral.referral_contact_id && referral.referral_contact_name) {
        setReferralContactDisplayLabel(referral.referral_contact_name);
      } else {
        setReferralContactDisplayLabel(null);
      }
      if (referral.insurance_company_id && referral.insurance_company_name) {
        setReferralInsuranceCompanyDisplayLabel(referral.insurance_company_name);
      } else {
        setReferralInsuranceCompanyDisplayLabel(null);
      }
      if (referral.insurance_type_id && referral.insurance_type_description) {
        setReferralInsuranceTypeDisplayLabel(referral.insurance_type_description);
      } else {
        setReferralInsuranceTypeDisplayLabel(null);
      }
      if (referral.referral_level_2_provider_id && referral.referral_level_2_provider_name) {
        setReferralLevel2ProviderDisplayLabel(referral.referral_level_2_provider_name);
      } else {
        setReferralLevel2ProviderDisplayLabel(null);
      }

      clearReferralUnsavedMarkers();

      if (referral.client_id) {
        loadClient(referral.client_id);
      }
    } catch (error) {
      console.error('Error loading referral:', error);
      alert('Failed to load referral');
      navigate(tenantPath('/lists/clients'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    if (isEdit && !isClosed && canUpdateReferral) {
      markReferralMainDirty();
    }
    const { name, type } = e.target;
    const value = type === 'checkbox' ? e.target.checked : e.target.value;
    const newFormData = {
      ...formData,
      [name]: value,
    };

    if (name === 'client_id') {
      newFormData.diagnosis_1_id = '';
    }

    // If close_date is cleared, clear termination_reason_id
    if (name === 'close_date' && !value) {
      newFormData.termination_reason_id = '';
    }
    // If referral_level_1 changes, clear referral_level_2 (Level 2 options depend on Level 1)
    if (name === 'referral_level_1_id') {
      newFormData.referral_level_2_id = '';
      newFormData.referral_level_2_provider_id = '';
    }
    // Unchecking Re-referral should clear Original SC
    if (name === 're_referral' && !value) {
      newFormData.original_sc_id = '';
    }
    // Unchecking Sibling in EI should clear Sibling's SC
    if (name === 'sibling_in_ei' && !value) {
      newFormData.sibling_sc_id = '';
    }

    setFormData(newFormData);

    if (name === 'referral_level_1_id') {
      setReferralLevel2ProviderDisplayLabel(null);
      setRl2ProviderSearchTerm('');
    }

    // If client_id changes, load client info
    if (name === 'client_id' && value) {
      loadClient(value);
    }
  };

  const handlePrimaryCareOfficeChange = useCallback(({ value, label, providerId }) => {
    if (isEdit && !isClosed && canUpdateReferral) {
      markReferralMainDirty();
    }
    const v = value != null ? String(value) : '';
    const isProviderSynthetic = v.startsWith('provider:');
    setFormData((prev) => ({
      ...prev,
      primary_care_physician_site_id: v,
      primary_care_physician_id:
        providerId !== '' && providerId != null
          ? String(providerId)
          : isProviderSynthetic
            ? v.replace(/^provider:/, '')
            : '',
    }));
    setReferralPcpDisplayLabel((label || '').trim() || null);
  }, [isEdit, isClosed, canUpdateReferral, markReferralMainDirty]);

  const rl2ProviderFilteredOptions = useMemo(() => {
    if (!rl2ProviderSearchTerm || String(rl2ProviderSearchTerm).trim().length < 2) return [];
    const q = String(rl2ProviderSearchTerm).trim().toLowerCase();
    const filtered = (pcpSiteOptions || []).filter((opt) => {
      const label = formatProviderSiteOptionLabel(opt);
      return label.toLowerCase().includes(q);
    });
    return [...filtered].sort((a, b) =>
      formatProviderSiteOptionLabel(a).localeCompare(formatProviderSiteOptionLabel(b), undefined, { sensitivity: 'base' })
    );
  }, [pcpSiteOptions, rl2ProviderSearchTerm]);

  const rl2ProviderFallbackOptions = useMemo(() => {
    const list = providers || [];
    const dr = list.filter((p) => String(p.provider_type_code || '').toUpperCase() === 'DR');
    return dr.length > 0 ? dr : list;
  }, [providers]);

  const saveReferralDraft = useCallback(() => {
    try {
      sessionStorage.setItem(REFERRAL_FORM_DRAFT_KEY, JSON.stringify({
        formData,
        referralPcpDisplayLabel: referralPcpDisplayLabel ?? null,
        referralContactDisplayLabel: referralContactDisplayLabel ?? null,
        referralLevel2ProviderDisplayLabel: referralLevel2ProviderDisplayLabel ?? null,
      }));
    } catch (_) {
      // ignore
    }
  }, [formData, referralPcpDisplayLabel, referralContactDisplayLabel, referralLevel2ProviderDisplayLabel]);

  // Referral Level 2 options filtered by selected Level 1, then A–Z by description
  const referralLevel2Options = useMemo(() => {
    const filtered = formData.referral_level_1_id
      ? referralLevel2.filter((r) => Number(r.referral_level_1_id) === Number(formData.referral_level_1_id))
      : [];
    return [...filtered].sort((a, b) =>
      String(a.description || '').localeCompare(String(b.description || ''), undefined, { sensitivity: 'base' })
    );
  }, [referralLevel2, formData.referral_level_1_id]);

  const selectedReferralLevel1 = useMemo(() => (
    referralLevel1.find((item) => String(item.id) === String(formData.referral_level_1_id))
  ), [referralLevel1, formData.referral_level_1_id]);

  // Require Referral Level 2 Provider only when Referral Source Level 1 is exactly "Provider" (not Physician, Doctor, etc.)
  const requiresReferralLevel2Provider = useMemo(() => {
    if (!selectedReferralLevel1) return false;
    const code = (selectedReferralLevel1.code || '').trim().toLowerCase();
    const desc = (selectedReferralLevel1.description || '').trim().toLowerCase();
    return code === 'provider' || desc === 'provider';
  }, [selectedReferralLevel1]);

  const validateForm = () => {
    const missing = [];
    const requiredFields = [
      ['client_id', 'Client'],
      ['open_date', 'Open / Referral Date'],
      ['intake_staff_id', 'Referral Completed By'],
      ['referral_level_1_id', 'Referral Source Level 1'],
      ['referral_contact_id', 'Referral Contact'],
      ['living_arrangement_id', 'Living Arrangement'],
      ['referral_reason', 'Referral Reason'],
      ['referral_phone', 'Referral Phone'],
    ];
    if (!isEdit) {
      requiredFields.splice(8, 0, ['insurance_company_id', 'Insurance Company'], ['insurance_type_id', 'Insurance Type']);
    }
    requiredFields.forEach(([key, label]) => {
      const value = formData[key];
      if (value === undefined || value === null || String(value).trim() === '') {
        missing.push(label);
      }
    });
    if (formData.re_referral && !formData.original_sc_id) {
      missing.push('Original SC (required for re-referral)');
    }
    if (formData.sibling_in_ei && !formData.sibling_sc_id) {
      missing.push("Sibling's SC (required when sibling is in EI)");
    }
    if (requiresReferralLevel2Provider && !formData.referral_level_2_provider_id) {
      missing.push('Referral Level 2 Provider');
    }
    if (formData.close_date && !formData.termination_reason_id) {
      missing.push('Termination Reason');
    }
    return missing;
  };

  const handleSaveTermination = async () => {
    if (!formData.close_date || !formData.termination_reason_id) {
      alert('Close date and termination reason are both required.');
      return;
    }
    setTerminationSaving(true);
    try {
      await referralAPI.updateTermination(referralId, {
        close_date: formData.close_date,
        termination_reason_id: parseInt(formData.termination_reason_id),
      });
      originalTerminationRef.current = {
        close_date: formData.close_date,
        termination_reason_id: formData.termination_reason_id,
      };
      setIsEditingTermination(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update termination details.');
    } finally {
      setTerminationSaving(false);
    }
  };

  const handleCancelTermination = () => {
    if (originalTerminationRef.current) {
      setFormData((prev) => ({
        ...prev,
        close_date: originalTerminationRef.current.close_date,
        termination_reason_id: originalTerminationRef.current.termination_reason_id,
      }));
    }
    setIsEditingTermination(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missing = validateForm();
    if (missing.length > 0) {
      alert(`Missing required fields: ${missing.join(', ')}`);
      return;
    }

    try {
      setLoading(true);
      
      // Check if we're closing the referral (adding close_date to an active referral)
      const wasClosed = isClosed;
      const willClose = !!formData.close_date;
      const isClosing = !wasClosed && willClose && isEdit;

      if (isClosing) {
        // Use the close endpoint for closing an active referral
        await referralAPI.close(referralId, {
          close_date: formData.close_date,
          termination_reason_id: formData.termination_reason_id ? parseInt(formData.termination_reason_id) : null,
        });
      } else {
        if (isEdit && canUpdateReferral && insuranceWorkspaceSaveRef.current) {
          try {
            await insuranceWorkspaceSaveRef.current();
          } catch (insErr) {
            const msg = insErr?.response?.data?.error || insErr?.message || 'Failed to save insurance';
            alert(msg);
            setLoading(false);
            return;
          }
        }
        // Regular update or create (update endpoint doesn't handle close_date/termination_reason_id)
        const openReferralDate = formData.open_date || null;
        const submitData = {
          ...formData,
          client_id: formData.client_id,
          referral_date: openReferralDate,
          open_date: openReferralDate,
          intake_staff_id: formData.intake_staff_id ? parseInt(formData.intake_staff_id) : null,
          referral_reason: formData.referral_reason || null,
          intake_notes: formData.intake_notes || null,
          referral_sc_comment: formData.referral_sc_comment || null,
          insurance_company_id: isEdit
            ? undefined
            : formData.insurance_company_id
              ? parseInt(formData.insurance_company_id, 10)
              : null,
          insurance_type_id: isEdit
            ? undefined
            : formData.insurance_type_id
              ? parseInt(formData.insurance_type_id, 10)
              : null,
          primary_care_physician_id: formData.primary_care_physician_id ? formData.primary_care_physician_id : null,
          // Send site id as string (integer or UUID); never parseInt — partial parses corrupt UUIDs.
          ...(() => {
            const raw = formData.primary_care_physician_site_id;
            if (raw == null || raw === '' || String(raw).startsWith('provider:')) {
              return { primary_care_physician_site_id: null, primary_care_physician_payee_site_id: null };
            }
            const siteKey = typeof raw === 'number' ? String(raw) : String(raw).trim();
            return {
              primary_care_physician_site_id: siteKey,
              primary_care_physician_payee_site_id: siteKey,
            };
          })(),
          living_arrangement_id: formData.living_arrangement_id ? parseInt(formData.living_arrangement_id) : null,
          referral_level_1_id: formData.referral_level_1_id ? parseInt(formData.referral_level_1_id) : null,
          referral_level_2_id: formData.referral_level_2_id ? parseInt(formData.referral_level_2_id) : null,
          household_size: formData.household_size !== '' ? parseInt(formData.household_size, 10) : null,
          referral_phone: formData.referral_phone || null,
          re_referral: formData.re_referral,
          original_sc_id: formData.original_sc_id ? parseInt(formData.original_sc_id, 10) : null,
          sibling_in_ei: formData.sibling_in_ei,
          sibling_sc_id: formData.sibling_sc_id ? parseInt(formData.sibling_sc_id, 10) : null,
          screened: formData.screened,
          referral_level_2_provider_id: formData.referral_level_2_provider_id || null,
          referral_contact_id: formData.referral_contact_id || null,
          referral_relationship_id: formData.referral_relationship_id ? parseInt(formData.referral_relationship_id, 10) : null,
          diagnosis_1_id: formData.diagnosis_1_id || null,
        };
        
        if (isEdit) {
          await referralAPI.update(referralId, submitData);
        } else {
          await referralAPI.create(submitData);
        }
      }
      
      try {
        sessionStorage.removeItem(REFERRAL_FORM_DRAFT_KEY);
      } catch (_) {
        // ignore
      }
      clearReferralUnsavedMarkers();
      // Stay on this referral after edit when opened from client → referral; otherwise go to client
      if (isEdit && isNestedUnderClient && clientIdFromPath && referralId) {
        navigate(tenantPath(`/client/${clientIdFromPath}/referral/${referralId}`), { replace: true });
      } else if (formData.client_id) {
        navigate(tenantPath(`/client/${formData.client_id}`));
      } else {
        navigate(tenantPath('/lists/clients'));
      }
    } catch (error) {
      console.error('Error saving referral:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save referral';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return <div className="loading">Loading form options...</div>;
  }

  if (showCreateDenied) {
    return (
      <div className="referral-form">
        <div className="referral-form-header">
          <div>
            <h1>{reReferralParam ? 'Re-Referral' : 'New Referral'}</h1>
          </div>
        </div>
        <div className="referral-access-denied" style={{ padding: '24px', textAlign: 'center', color: '#721c24', background: '#f8d7da', borderRadius: 8, margin: 20 }}>
          <p>You don&apos;t have permission to create referrals.</p>
        </div>
      </div>
    );
  }

  const referralSubmitLabel = loading ? 'Saving…' : isEdit ? 'Save' : 'Create Referral';

  return (
    <div className="referral-form">
      <div className="referral-form-header">
        <div>
          <h1>{isEdit ? 'Edit Referral' : (reReferralParam ? 'Re-Referral' : 'New Referral')}</h1>
        </div>
      </div>

      {selectedClient && (
        <div className="client-info-banner">
          <strong>Client:</strong> {selectedClient.last_name}, {selectedClient.first_name}
          {selectedClient.date_of_birth && (
            <span> • DOB: {formatDisplayDate(selectedClient.date_of_birth)}</span>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-actions referral-form-actions-top">
          <button type="button" onClick={() => navigate(-1)} className="btn-cancel">
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading || isEditingTermination}>
            {referralSubmitLabel}
          </button>
        </div>

        <div className="form-section">
          <h2 className="section-title">Referral Information</h2>
          <div className="form-grid">
            <div className="referral-form-row-4 referral-form-row-4-top">
            <div className="form-group referral-form-client-select-wrap">
              <label htmlFor="referral-form-client-search">
                Client <span className="required">*</span>
              </label>
              {!!clientId || isEdit ? (
                <div className="referral-form-selected-value referral-form-client-value-wide" aria-live="polite">
                  {selectedClient
                    ? `${selectedClient.last_name}, ${selectedClient.first_name}`
                    : formData.client_id
                      ? '—'
                      : ''}
                </div>
              ) : formData.client_id && selectedClient ? (
                <div className="referral-form-searchable-selected">
                  <span className="referral-form-selected-label referral-form-client-value-wide">
                    {selectedClient.last_name}, {selectedClient.first_name}
                  </span>
                  <button
                    type="button"
                    className="referral-form-btn-change"
                    onClick={() => {
                      markReferralMainDirty();
                      setFormData((prev) => ({ ...prev, client_id: '', diagnosis_1_id: '' }));
                      referralDxPrefillKeyRef.current = '';
                      priorReferralPrefillClientIdRef.current = null;
                      setSelectedClient(null);
                      setClientSearch('');
                      setClientResults([]);
                    }}
                    title="Change"
                    aria-label="Change"
                  >
                    <svg className="referral-form-btn-change-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="referral-form-searchable-wrap">
                  <input
                    id="referral-form-client-search"
                    type="text"
                    className="referral-form-searchable-input"
                    placeholder="Type to search clients (name or EI number)..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    onFocus={() => setClientDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setClientDropdownOpen(false), 200)}
                    autoComplete="off"
                    aria-expanded={clientDropdownOpen}
                    aria-controls="referral-form-client-results"
                    aria-autocomplete="list"
                  />
                  {clientSearchLoading && <span className="referral-form-searchable-loading" aria-live="polite">Searching…</span>}
                  {clientDropdownOpen && (clientSearch.length >= 2 || clientResults.length > 0) && (
                    <ul id="referral-form-client-results" className="referral-form-searchable-results referral-form-client-results" role="listbox">
                      {clientResults.length === 0 && !clientSearchLoading && (
                        <li className="referral-form-searchable-no-results">Type at least 2 characters to search.</li>
                      )}
                      {clientResults.map((c) => (
                        <li
                          key={c.id}
                          role="option"
                          className="referral-form-searchable-option"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            referralDxPrefillKeyRef.current = '';
                            priorReferralPrefillClientIdRef.current = null;
                            markReferralMainDirty();
                            setFormData((prev) => ({ ...prev, client_id: c.id, diagnosis_1_id: '' }));
                            loadClient(c.id);
                            setClientSearch('');
                            setClientResults([]);
                            setClientDropdownOpen(false);
                          }}
                        >
                          {c.last_name}, {c.first_name}
                          {c.ei_number && ` · ${c.ei_number}`}
                          {c.date_of_birth && ` · DOB ${formatDisplayDate(c.date_of_birth)}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <input type="hidden" name="client_id" value={formData.client_id || ''} readOnly aria-hidden="true" tabIndex={-1} />
            </div>

            <div className="form-group">
              <label htmlFor="open_date" className="referral-form-label-with-info">
                Open / Referral Date <span className="required">*</span>
                <button
                  type="button"
                  className="referral-form-info-icon"
                  onClick={() => setShowOpenReferralDateInfo((v) => !v)}
                  aria-expanded={showOpenReferralDateInfo}
                  aria-controls="referral-form-info-open-referral-date"
                  aria-label={showOpenReferralDateInfo ? 'Hide explanation' : 'Show explanation'}
                >
                  ⓘ
                </button>
              </label>
              {showOpenReferralDateInfo && (
                <p id="referral-form-info-open-referral-date" className="referral-form-info-text">
                  When the referral is taken, the clock starts; this is the start of the episode of care.
                </p>
              )}
              <input
                type="date"
                id="open_date"
                name="open_date"
                value={formData.open_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="intake_staff_id">Referral Completed By <span className="required">*</span></label>
              <select
                id="intake_staff_id"
                name="intake_staff_id"
                value={formData.intake_staff_id}
                onChange={handleChange}
                required
              >
                <option value="">Select staff...</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.last_name}, {s.first_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="referral_status">Referral Status</label>
              <select
                id="referral_status"
                name="referral_status"
                value={formData.referral_status}
                onChange={handleChange}
                disabled={isClosed}
              >
                <option value="incomplete">Incomplete</option>
                <option value="waiting">Waiting</option>
                <option value="active">Active</option>
                <option value="closing_out">Closing Out</option>
                <option value="transferring_out">Transferring Out</option>
                <option value="transferring_in">Transferring In</option>
              </select>
            </div>
            </div>

            <div className="referral-form-row-4 referral-form-row-4-referral-source">
              <div className="form-group">
                <label htmlFor="referral_level_1_id">Referral Source Level 1 <span className="required">*</span></label>
                <select
                  id="referral_level_1_id"
                  name="referral_level_1_id"
                  value={formData.referral_level_1_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  {referralLevel1.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="referral_level_2_id">Referral Source Level 2</label>
                <select
                  id="referral_level_2_id"
                  name="referral_level_2_id"
                  value={formData.referral_level_2_id}
                  onChange={handleChange}
                  disabled={!formData.referral_level_1_id}
                >
                  <option value="">Select Referral Source Level 1 first...</option>
                  {referralLevel2Options.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group referral-form-contact-select-wrap referral-form-rl2-provider-wrap">
                <label htmlFor="referral-form-rl2-provider-search">
                  Referral Level 2 Provider
                  {requiresReferralLevel2Provider ? <span className="required"> *</span> : null}
                </label>
                {Array.isArray(pcpSiteOptions) && pcpSiteOptions.length > 0 ? (
                  formData.referral_level_2_provider_id ? (
                    <div className="referral-form-searchable-selected referral-form-rl2-provider-selected">
                      <span
                        className="referral-form-selected-label referral-form-rl2-provider-selected-label"
                        role="button"
                        tabIndex={0}
                        title="Choose a different provider"
                        onClick={() => {
                          markReferralMainDirty();
                          setFormData((prev) => ({ ...prev, referral_level_2_provider_id: '' }));
                          setReferralLevel2ProviderDisplayLabel(null);
                          setRl2ProviderSearchTerm('');
                          setRl2ProviderDropdownOpen(true);
                          requestAnimationFrame(() => rl2ProviderInputRef.current?.focus());
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return;
                          e.preventDefault();
                          markReferralMainDirty();
                          setFormData((prev) => ({ ...prev, referral_level_2_provider_id: '' }));
                          setReferralLevel2ProviderDisplayLabel(null);
                          setRl2ProviderSearchTerm('');
                          setRl2ProviderDropdownOpen(true);
                          requestAnimationFrame(() => rl2ProviderInputRef.current?.focus());
                        }}
                      >
                        {(() => {
                          const siteOpt = pcpSiteOptions.find((o) => String(o.provider_id) === String(formData.referral_level_2_provider_id));
                          if (siteOpt) return formatProviderSiteOptionLabel(siteOpt);
                          const p = providers.find((x) => String(x.id) === String(formData.referral_level_2_provider_id));
                          if (p) return formatProviderDisplayLabel(p);
                          return referralLevel2ProviderDisplayLabel || formData.referral_level_2_provider_id;
                        })()}
                      </span>
                      <div className="referral-form-field-icons">
                        <button
                          type="button"
                          className="referral-form-btn-view"
                          onClick={() => navigate(tenantPath(`/provider/${formData.referral_level_2_provider_id}`), { state: clientIdFromPath ? { returnToClientId: clientIdFromPath } : {} })}
                          title="View provider"
                          aria-label="View provider"
                        >
                          <svg className="referral-form-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="referral-form-searchable-wrap">
                      <input
                        ref={rl2ProviderInputRef}
                        id="referral-form-rl2-provider-search"
                        type="text"
                        className="referral-form-searchable-input"
                        placeholder="Type 2+ letters to search doctors (with site)…"
                        value={rl2ProviderSearchTerm}
                        onChange={(e) => setRl2ProviderSearchTerm(e.target.value)}
                        onFocus={() => { if (rl2ProviderCloseTimerRef.current) clearTimeout(rl2ProviderCloseTimerRef.current); setRl2ProviderDropdownOpen(true); }}
                        onBlur={() => { rl2ProviderCloseTimerRef.current = setTimeout(() => setRl2ProviderDropdownOpen(false), 200); }}
                        autoComplete="off"
                        aria-expanded={rl2ProviderDropdownOpen}
                        aria-controls="referral-form-rl2-provider-results"
                        aria-autocomplete="list"
                      />
                      {rl2ProviderDropdownOpen && (rl2ProviderSearchTerm.trim().length >= 2 || rl2ProviderFilteredOptions.length > 0) && (
                        <ul id="referral-form-rl2-provider-results" className="referral-form-searchable-results" role="listbox">
                          {rl2ProviderFilteredOptions.length === 0 && (
                            <li className="referral-form-searchable-no-results">No matching doctors. Try another spelling.</li>
                          )}
                          {rl2ProviderFilteredOptions.map((opt) => (
                            <li
                              key={`${opt.provider_id}-${opt.payee_site_id ?? opt.provider_site_id}`}
                              role="option"
                              className="referral-form-searchable-option"
                              onPointerDown={(e) => {
                                e.preventDefault();
                                markReferralMainDirty();
                                setFormData((prev) => ({ ...prev, referral_level_2_provider_id: String(opt.provider_id) }));
                                setReferralLevel2ProviderDisplayLabel(formatProviderSiteOptionLabel(opt));
                                setRl2ProviderSearchTerm('');
                                setRl2ProviderDropdownOpen(false);
                              }}
                            >
                              {formatProviderSiteOptionLabel(opt)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )
                ) : (
                  <select
                    id="referral_level_2_provider_id"
                    name="referral_level_2_provider_id"
                    value={formData.referral_level_2_provider_id}
                    onChange={handleChange}
                    required={requiresReferralLevel2Provider}
                  >
                    <option value="">Select provider...</option>
                    {rl2ProviderFallbackOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {formatProviderDisplayLabel(p)}
                      </option>
                    ))}
                    {formData.referral_level_2_provider_id &&
                     !rl2ProviderFallbackOptions.some((p) => String(p.id) === String(formData.referral_level_2_provider_id)) && (
                      <option value={formData.referral_level_2_provider_id}>
                        {referralLevel2ProviderDisplayLabel || formData.referral_level_2_provider_id}
                      </option>
                    )}
                  </select>
                )}
                {Array.isArray(pcpSiteOptions) && pcpSiteOptions.length > 0 ? (
                  <input type="hidden" name="referral_level_2_provider_id" value={formData.referral_level_2_provider_id || ''} readOnly aria-hidden="true" tabIndex={-1} />
                ) : null}
              </div>
              <div className="form-group">
                <label className="referral-form-checkbox-label">
                  <input
                    type="checkbox"
                    name="screened"
                    checked={!!formData.screened}
                    onChange={handleChange}
                  />
                  <span>Screened</span>
                </label>
              </div>
            </div>

            <div className="referral-form-row-2 referral-form-row-contact-relationship">
              <div className="form-group referral-form-contact-select-wrap">
                <div className="referral-form-contact-label-row">
                  <label htmlFor="referral-form-contact-search">
                    Referral Contact <span className="required">*</span>
                  </label>
                  {canCreateContact && (
                    <button
                      type="button"
                      className="referral-form-btn-new-provider referral-form-btn-new-contact"
                      onClick={() => {
                        if (!isEdit) saveReferralDraft();
                        navigate(tenantPath('/contact/new'), { state: { returnToPath: location.pathname + location.search } });
                      }}
                      title="Create a new contact in the system, then return here to select them"
                      aria-label="Add new contact"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                  )}
                </div>
                {formData.referral_contact_id ? (
                  <div className="referral-form-searchable-selected">
                    <span className="referral-form-selected-label">
                      {(() => {
                        const c = contactResults.find((x) => String(x.id) === String(formData.referral_contact_id))
                          || contacts.find((x) => String(x.id) === String(formData.referral_contact_id));
                        return c ? [c.last_name, c.first_name].filter(Boolean).join(', ') || c.id : referralContactDisplayLabel || formData.referral_contact_id;
                      })()}
                    </span>
                    <div className="referral-form-field-icons">
                      <button
                        type="button"
                        className="referral-form-btn-view"
                        onClick={() => navigate(clientIdFromPath ? tenantPath(`/client/${clientIdFromPath}/contact/${formData.referral_contact_id}`) : tenantPath(`/contact/${formData.referral_contact_id}`), { state: clientIdFromPath ? { returnToClientId: clientIdFromPath } : {} })}
                        title="View contact"
                        aria-label="View contact"
                      >
                        <svg className="referral-form-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="referral-form-btn-change"
                        onClick={() => {
                          markReferralMainDirty();
                          setFormData((prev) => ({ ...prev, referral_contact_id: '' }));
                          setReferralContactDisplayLabel(null);
                          setContactSearch('');
                          setContactResults([]);
                        }}
                        title="Change"
                        aria-label="Change"
                      >
                        <svg className="referral-form-btn-change-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="referral-form-searchable-wrap">
                    <input
                      id="referral-form-contact-search"
                      type="text"
                      className="referral-form-searchable-input"
                      placeholder="Type to search contacts..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      onFocus={() => { if (contactCloseTimerRef.current) clearTimeout(contactCloseTimerRef.current); setContactDropdownOpen(true); }}
                      onBlur={() => { contactCloseTimerRef.current = setTimeout(() => setContactDropdownOpen(false), 200); }}
                      autoComplete="off"
                      aria-expanded={contactDropdownOpen}
                      aria-controls="referral-form-contact-results"
                      aria-autocomplete="list"
                    />
                    {contactSearchLoading && <span className="referral-form-searchable-loading" aria-live="polite">Searching…</span>}
                    {contactDropdownOpen && (contactSearch.length >= 2 || contactResults.length > 0) && (
                      <ul id="referral-form-contact-results" className="referral-form-searchable-results" role="listbox">
                        {contactResults.length === 0 && !contactSearchLoading && (
                          <li className="referral-form-searchable-no-results">Type at least 2 characters to search.</li>
                        )}
                        {contactResults.map((c) => (
                          <li
                            key={c.id}
                            role="option"
                            className="referral-form-searchable-option"
                            onPointerDown={(e) => {
                              e.preventDefault();
                              markReferralMainDirty();
                              setFormData((prev) => ({ ...prev, referral_contact_id: c.id }));
                              setReferralContactDisplayLabel([c.last_name, c.first_name].filter(Boolean).join(', ') || c.id);
                              setContactSearch('');
                              setContactResults([]);
                              setContactDropdownOpen(false);
                            }}
                          >
                            {[c.last_name, c.first_name].filter(Boolean).join(', ') || c.id}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <input type="hidden" name="referral_contact_id" value={formData.referral_contact_id || ''} readOnly aria-hidden="true" tabIndex={-1} />
              </div>
              <div className="form-group">
                <label htmlFor="referral_phone">Referral Phone <span className="required">*</span></label>
                <input
                  type="tel"
                  id="referral_phone"
                  name="referral_phone"
                  value={formData.referral_phone}
                  onChange={handleChange}
                  placeholder="e.g. 630-659-8384"
                  required
                />
              </div>
            </div>

            <div className="referral-form-row-2">
              <div className="form-group">
                <label htmlFor="living_arrangement_id">Living Arrangement <span className="required">*</span></label>
                <select
                  id="living_arrangement_id"
                  name="living_arrangement_id"
                  value={formData.living_arrangement_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  {livingArrangements.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="household_size">Household Size</label>
                <input
                  type="number"
                  id="household_size"
                  name="household_size"
                  value={formData.household_size}
                  onChange={handleChange}
                  min="1"
                  max="99"
                  placeholder="e.g. 4"
                />
              </div>
            </div>

            <div className="form-group form-group-full">
              <label htmlFor="intake_notes">Referral Notes</label>
              <textarea
                id="intake_notes"
                name="intake_notes"
                value={formData.intake_notes}
                onChange={handleChange}
                className="referral-form-textarea-single"
                rows={1}
                placeholder="Additional referral notes..."
              />
            </div>

            <div className="form-group form-group-full">
              <label htmlFor="referral_sc_comment">Referral SC Comment</label>
              <textarea
                id="referral_sc_comment"
                name="referral_sc_comment"
                value={formData.referral_sc_comment}
                onChange={handleChange}
                className="referral-form-textarea-single"
                rows={1}
                placeholder="SC comment (included in assignment notification)..."
              />
            </div>

            <div className="form-group form-group-full">
              <label htmlFor="referral_reason">Referral Reason <span className="required">*</span></label>
              <textarea
                id="referral_reason"
                name="referral_reason"
                value={formData.referral_reason}
                onChange={handleChange}
                className="referral-form-textarea-single"
                rows={1}
                placeholder="Reason for referral..."
                required
              />
            </div>

            <div className="referral-form-row-2">
              <div className="form-group">
                <label htmlFor="diagnosis_1_id">Diagnosis Dx1</label>
                <select
                  id="diagnosis_1_id"
                  name="diagnosis_1_id"
                  value={formData.diagnosis_1_id}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {diagnosisAutoeligible.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group referral-form-pcp-wrap">
              <div className="referral-form-pcp-label-row">
                <label htmlFor="referral-form-primary-care-office">Primary Care Physician</label>
                {canCreateProvider && (
                  <button
                    type="button"
                    className="referral-form-btn-new-provider"
                    onClick={() => {
                      if (!isEdit) saveReferralDraft();
                      navigate(tenantPath('/provider/new'), { state: { returnToPath: location.pathname + location.search } });
                    }}
                    title="Create a new provider in the system, then return here to select them"
                    aria-label="Add new provider"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="referral-form-pcp-select-wrap">
                <SearchableProviderTeamOfficeSelect
                  id="referral-form-primary-care-office"
                  typeCode="DR"
                  formatOptionLabel={formatProviderSiteOptionLabel}
                  hiddenInputName="primary_care_physician_site_id"
                  value={
                    formData.primary_care_physician_site_id != null && formData.primary_care_physician_site_id !== ''
                      ? String(formData.primary_care_physician_site_id)
                      : formData.primary_care_physician_id
                        ? `provider:${formData.primary_care_physician_id}`
                        : ''
                  }
                  displayLabel={(() => {
                    const site = formData.primary_care_physician_site_id;
                    if (site != null && String(site).startsWith('provider:') && referralPcpDisplayLabel) {
                      return `${referralPcpDisplayLabel} (no site)`;
                    }
                    return referralPcpDisplayLabel || '';
                  })()}
                  onChange={handlePrimaryCareOfficeChange}
                  required
                  placeholder="Click and type PCP last name, first name, or DR…"
                  searchPlaceholder="Last name, first name, or type code…"
                  resultHint="Search doctors (DR) by name or type code — up to 25 offices."
                />
                {formData.primary_care_physician_id && (
                  <button
                    type="button"
                    className="referral-form-btn-view referral-form-btn-view-after-select"
                    onClick={() => navigate(tenantPath(`/provider/${formData.primary_care_physician_id}`), { state: clientIdFromPath ? { returnToClientId: clientIdFromPath } : {} })}
                    title="View provider"
                    aria-label="View provider"
                  >
                    <svg className="referral-form-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            </div>

            {!isEdit && (
            <div className="referral-form-row-3">
              <div className="form-group">
                <div className="referral-form-pcp-label-row">
                  <label htmlFor="insurance_company_id">Insurance Company <span className="required">*</span></label>
                  {canManageInsuranceCompaniesLookup && (
                    <button
                      type="button"
                      className="referral-form-btn-new-provider"
                      onClick={() => {
                        if (!isEdit) saveReferralDraft();
                        navigate(tenantPath('/settings/insurance-companies'), {
                          state: { returnToPath: `${location.pathname}${location.search || ''}` },
                        });
                      }}
                      title="Open Insurance Companies in Settings to add or edit companies, then return here"
                      aria-label="Open insurance companies lookup"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                  )}
                </div>
                <select
                  id="insurance_company_id"
                  name="insurance_company_id"
                  value={formData.insurance_company_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  {insuranceCompanies.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                  {formData.insurance_company_id && referralInsuranceCompanyDisplayLabel &&
                   !insuranceCompanies.some((item) => String(item.id) === String(formData.insurance_company_id)) && (
                    <option value={formData.insurance_company_id}>
                      {referralInsuranceCompanyDisplayLabel}
                    </option>
                  )}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="insurance_type_id">Insurance Type <span className="required">*</span></label>
                <select
                  id="insurance_type_id"
                  name="insurance_type_id"
                  value={formData.insurance_type_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  {insuranceTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.description}
                    </option>
                  ))}
                  {formData.insurance_type_id && referralInsuranceTypeDisplayLabel &&
                   !insuranceTypes.some((item) => String(item.id) === String(formData.insurance_type_id)) && (
                    <option value={formData.insurance_type_id}>
                      {referralInsuranceTypeDisplayLabel}
                    </option>
                  )}
                </select>
              </div>
            </div>
            )}

            {isEdit && referralId && (
              <ReferralInsuranceWorkspaceSection
                referralId={referralId}
                clientId={formData.client_id || ''}
                insuranceCompanies={insuranceCompanies}
                insuranceTypes={insuranceTypes}
                readOnly={isClosed || !canUpdateReferral}
                registerSaveHandler={isClosed || !canUpdateReferral ? undefined : registerInsuranceWorkspaceSave}
                onWorkspaceDirtyChange={
                  isClosed || !canUpdateReferral ? undefined : handleInsuranceWorkspaceDirtyChange
                }
                canManageInsuranceCompaniesLookup={canManageInsuranceCompaniesLookup}
                referralFormPathForInsuranceLookup={`${location.pathname}${location.search || ''}`}
              />
            )}

            <div className="referral-form-row-4">
              <div className="form-group">
                <label className="referral-form-checkbox-label">
                  <input
                    type="checkbox"
                    name="re_referral"
                    checked={!!formData.re_referral}
                    onChange={handleChange}
                  />
                  <span>Re-referral</span>
                  <button
                    type="button"
                    className="referral-form-info-icon"
                    onClick={() => setShowReReferralInfo((v) => !v)}
                    aria-expanded={showReReferralInfo}
                    aria-controls="referral-form-info-rereferral"
                    aria-label={showReReferralInfo ? 'Hide explanation' : 'Show explanation'}
                  >
                    ⓘ
                  </button>
                </label>
                {showReReferralInfo && (
                  <p id="referral-form-info-rereferral" className="referral-form-info-text">
                    This episode is a re-referral (client returning to EI).
                  </p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="original_sc_id" className="referral-form-label-with-info">
                  Original SC (if re-referral)
                  {formData.re_referral ? <span className="required"> *</span> : null}
                  <button
                    type="button"
                    className="referral-form-info-icon"
                    onClick={() => setShowOriginalScInfo((v) => !v)}
                    aria-expanded={showOriginalScInfo}
                    aria-controls="referral-form-info-original-sc"
                    aria-label={showOriginalScInfo ? 'Hide explanation' : 'Show explanation'}
                  >
                    ⓘ
                  </button>
                </label>
                {showOriginalScInfo && (
                  <p id="referral-form-info-original-sc" className="referral-form-info-text">
                    Service coordinator originally assigned when first referred.
                  </p>
                )}
                {formData.re_referral ? (
                  <select
                    id="original_sc_id"
                    name="original_sc_id"
                    value={formData.original_sc_id}
                    onChange={handleChange}
                    required={!!formData.re_referral}
                  >
                    <option value="">Select staff...</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.last_name}, {s.first_name}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
              <div className="form-group">
                <label className="referral-form-checkbox-label">
                  <input
                    type="checkbox"
                    name="sibling_in_ei"
                    checked={!!formData.sibling_in_ei}
                    onChange={handleChange}
                  />
                  <span>Sibling in EI</span>
                  <button
                    type="button"
                    className="referral-form-info-icon"
                    onClick={() => setShowSiblingInEiInfo((v) => !v)}
                    aria-expanded={showSiblingInEiInfo}
                    aria-controls="referral-form-info-sibling-ei"
                    aria-label={showSiblingInEiInfo ? 'Hide explanation' : 'Show explanation'}
                  >
                    ⓘ
                  </button>
                </label>
                {showSiblingInEiInfo && (
                  <p id="referral-form-info-sibling-ei" className="referral-form-info-text">
                    This client has a sibling currently in EI.
                  </p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="sibling_sc_id" className="referral-form-label-with-info">
                  Sibling&apos;s SC
                  {formData.sibling_in_ei ? <span className="required"> *</span> : null}
                  <button
                    type="button"
                    className="referral-form-info-icon"
                    onClick={() => setShowSiblingScInfo((v) => !v)}
                    aria-expanded={showSiblingScInfo}
                    aria-controls="referral-form-info-sibling-sc"
                    aria-label={showSiblingScInfo ? 'Hide explanation' : 'Show explanation'}
                  >
                    ⓘ
                  </button>
                </label>
                {showSiblingScInfo && (
                  <p id="referral-form-info-sibling-sc" className="referral-form-info-text">
                    Assigned SC of the sibling in EI.
                  </p>
                )}
                {formData.sibling_in_ei ? (
                  <select
                    id="sibling_sc_id"
                    name="sibling_sc_id"
                    value={formData.sibling_sc_id}
                    onChange={handleChange}
                    required={!!formData.sibling_in_ei}
                  >
                    <option value="">Select staff...</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.last_name}, {s.first_name}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {isEdit && (
          <div className="form-section">
            <div className="section-title-row">
              <h2 className="section-title">Close referral</h2>
              {isClosed && canEditTermination && !isEditingTermination && (
                <button
                  type="button"
                  className="btn-link-inline"
                  onClick={() => setIsEditingTermination(true)}
                >
                  Edit
                </button>
              )}
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="close_date">Close Date</label>
                <input
                  type="date"
                  id="close_date"
                  name="close_date"
                  value={formData.close_date}
                  onChange={handleChange}
                  disabled={isClosed && !isEditingTermination}
                />
                <small>
                  {isClosed && !isEditingTermination
                    ? 'Referral is already closed'
                    : 'Date this episode of care ends (requires termination reason)'}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="termination_reason_id">
                  Termination Reason
                  {formData.close_date && <span className="required">*</span>}
                </label>
                <select
                  id="termination_reason_id"
                  name="termination_reason_id"
                  value={formData.termination_reason_id}
                  onChange={handleChange}
                  disabled={(isClosed && !isEditingTermination) || !formData.close_date}
                  required={!!formData.close_date}
                >
                  <option value="">Select a reason...</option>
                  {terminationReasons && terminationReasons.length > 0 ? (
                    terminationReasons.map((reason) => (
                      <option key={reason.id} value={reason.id}>
                        {reason.code} - {reason.description}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No termination reasons available</option>
                  )}
                </select>
                <small>
                  {!formData.close_date && 'Required when closing a referral'}
                  {formData.close_date && terminationReasons.length === 0 && ' (No termination reasons found - check database)'}
                </small>
              </div>
            </div>
            {isEditingTermination && (
              <div className="form-actions" style={{ marginTop: '0.75rem' }}>
                <button type="button" className="btn-cancel" onClick={handleCancelTermination} disabled={terminationSaving}>
                  Cancel
                </button>
                <button type="button" className="btn-save" onClick={handleSaveTermination} disabled={terminationSaving}>
                  {terminationSaving ? 'Saving…' : 'Save termination'}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="form-actions referral-form-actions-bottom">
          <button type="button" onClick={() => navigate(-1)} className="btn-cancel">
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading || isEditingTermination}>
            {referralSubmitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ReferralForm;
